import type { UnitSystem } from '@lift2fit/shared-types';
import { kgToLb } from './units';

/** Tiny className joiner — avoids a runtime dependency for a 6-line utility. */
export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** An exact whole-number count, grouped for readability (`1,234`). */
export function formatCount(value: number): string {
  return value.toLocaleString('en-GB');
}

/**
 * Exact money, in the account's currency. The one money formatter — the ledger,
 * payment tables and every detail read use it, because those are the surfaces a
 * coach reconciles against.
 */
export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/* ---------------------------------------------------------------------------
   Compact figures for summary boxes.

   A headline tile has one line and a fixed width, so an exact, ever-growing
   figure (`£1,234,567.89`, `2,413,260 kg`) eventually outgrows it. The tile now
   renders a bounded magnitude instead — raw below 1,000, then K / M / B — while
   the exact value stays on the ledger, in every table cell that IS the record,
   and in the element's `title` so hovering still gives the precise figure.

   The rules, settled deliberately:

   - Below 1,000 the figure is raw and grouped (`£945.00`, `12`). Compacting
     `945` to `0.9K` would be worse, not better: it loses the pence a coach
     scans for and the exactness of a small, common number.
   - A compacted magnitude carries at most three significant digits: one decimal
     below ten of its unit (`1.2K`), none at or above (`12K`, `987K`). That
     bounds the rendered string to seven characters, which is what makes
     containment provable rather than hopeful. Rounding that crosses a unit is
     promoted, so `999,999` reads `1M`, never `1000K`.
   - Money keeps its symbol (`£1.2K`), via the locale's own currency part, and
     drops its minor unit above the threshold. Pence are not "lost": the exact
     value is one hover, one ledger row or one table cell away, and money that
     is reconciled row-by-row is never compacted in the first place.
   ------------------------------------------------------------------------ */

/** Below this, raw and grouped; at or above it, K / M / B. */
const COMPACT_THRESHOLD = 1_000;

/**
 * A bounded magnitude for a count in a summary box (`1.2K`, `12K`, `1M`).
 * Exact below 1,000, so small figures are unchanged.
 */
export function formatCompactCount(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs < COMPACT_THRESHOLD) return `${sign}${Math.round(abs).toLocaleString('en-GB')}`;

  // The largest unit the value fills, chosen ascending so a value never reads
  // as `10000K`.
  const unit =
    abs >= 1_000_000_000
      ? { size: 1_000_000_000, suffix: 'B' }
      : abs >= 1_000_000
        ? { size: 1_000_000, suffix: 'M' }
        : { size: 1_000, suffix: 'K' };

  let scaled = abs / unit.size;
  let suffix = unit.suffix;
  let text = scaled.toFixed(scaled < 10 ? 1 : 0);
  if (Number(text) >= COMPACT_THRESHOLD) {
    // Rounding crossed the next unit: 999,999 is `1M`, never `1000K`.
    const up =
      unit.size === 1_000
        ? { size: 1_000_000, suffix: 'M' }
        : unit.size === 1_000_000
          ? { size: 1_000_000_000, suffix: 'B' }
          : unit;
    scaled = abs / up.size;
    suffix = up.suffix;
    text = scaled.toFixed(scaled < 10 ? 1 : 0);
  }
  return `${sign}${text.replace(/\.0$/, '')}${suffix}`;
}

/** The currency's own symbol (`£`, `€`, `$`), or null when it has none. */
function currencySymbol(currency: string): string | null {
  try {
    const parts = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? null;
  } catch {
    return null;
  }
}

/**
 * Money in a summary box: exact below 1,000, then a symbol-bearing magnitude
 * (`£1.2K`, `£12K`, `£1.3M`). The exact value belongs in the `title` and one
 * click away in the ledger.
 */
export function formatCompactMoney(amount: number, currency: string): string {
  if (!Number.isFinite(amount)) return '—';
  if (Math.abs(amount) < COMPACT_THRESHOLD) return formatMoney(amount, currency);
  const magnitude = formatCompactCount(amount);
  const symbol = currencySymbol(currency);
  if (symbol === null) return `${currency} ${magnitude}`;
  const negative = magnitude.startsWith('-');
  return `${negative ? '-' : ''}${symbol}${negative ? magnitude.slice(1) : magnitude}`;
}

/**
 * Volume in a summary tile — a count in the display unit, compacted the same
 * way, so a large block is `2.4M kg` instead of a number that leaves the box.
 */
export function formatCompactVolume(volumeKg: number, unit: UnitSystem): string {
  const value = unit === 'imperial' ? kgToLb(volumeKg) : volumeKg;
  return `${formatCompactCount(value)} ${unit === 'imperial' ? 'lb' : 'kg'}`;
}

/** "12-week streak" / "1-week streak" */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function formatPercent(value: number, digits = 1): string {
  const rounded = Number(value.toFixed(digits));
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/** "3 min ago" — used by the sync chip */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const seconds = Math.max(1, Math.floor((now.getTime() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function uid(prefix = 'l2f'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(16).slice(2, 10);
  return `${prefix}-${rand}`;
}

/**
 * The two-letter monogram for the account button's avatar.
 *
 * The account control in the nav is a fixed size, so the value it renders must
 * be bounded: never more than two code points, whatever the name. Words are
 * whitespace-separated, so:
 *
 *   "Alex Johnson"      -> "AJ"    "Mary Jane Watson" -> "MJ"
 *   "Madonna"           -> "MA"    (a single word: its first two letters)
 *   "Jean-Luc Picard"   -> "JP"    "O'Brien"          -> "OB"
 *   "张伟"               -> "张伟"  (code points, not UTF-16 units)
 *   ""                  -> the email's first letter, else "?"
 *
 * Punctuation is never returned, so an apostrophe can never be a "letter". This
 * is presentation only: the button's accessible name still carries the full
 * display name, so a screen reader never hears the abbreviation.
 */
export function displayInitials(displayName?: string | null, email?: string | null): string {
  const name = (displayName ?? '').trim();
  /** Letters and digits only — `\p{L}` covers non-Latin scripts. */
  const letters = (value: string): string[] => Array.from(value).filter((char) => /[\p{L}\p{N}]/u.test(char));
  const firstLetter = (value: string): string => letters(value)[0] ?? '';

  let initials: string;
  if (name) {
    const words = name.split(/\s+/u).filter(Boolean);
    initials =
      words.length > 1
        ? `${firstLetter(words[0] ?? '')}${firstLetter(words[1] ?? '')}`
        : letters(words[0] ?? '').slice(0, 2).join('');
  } else {
    // No name to work from: one letter of the email local-part beats an empty
    // avatar. `split('@')[0]` is always defined, but the guard keeps TS strict.
    initials = firstLetter((email ?? '').trim().split('@')[0] ?? '');
  }

  return initials.toLocaleUpperCase() || '?';
}


